import { Box } from '@mui/material';
import { AutocompleteInput } from 'react-admin';
import { useChoicesContext, useSourceContext } from 'ra-core';
import { useWatch } from 'react-hook-form';
import { useCallback, useMemo, useState } from 'react';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import { MemberLight } from '@wwf/sdk';
import { memberOptionText } from '@app/member/MemberInputOption';

const emptyInputText = () => '';

export const MissionContactAutocompleteInput = (props: {
    disabled?: boolean;
    label?: string;
    helperText?: string | false;
}) => {
    const { getSource } = useSourceContext();
    const { allChoices } = useChoicesContext();
    const contactId = useWatch({ name: getSource('contactId') });
    const [focused, setFocused] = useState(false);

    const selected = allChoices?.find(
        c => String(c.id) === String(contactId)
    ) as MemberLight | undefined;
    const showCardOverlay = Boolean(selected && !focused);

    const handleFocus = useCallback(() => setFocused(true), []);
    const handleBlur = useCallback(() => setFocused(false), []);

    const handleChange = useCallback(
        (_value: unknown, record: MemberLight | '') => {
            if (record && typeof record === 'object') {
                queueMicrotask(() => {
                    const el = document.activeElement;
                    if (el instanceof HTMLElement) el.blur();
                });
            }
        },
        []
    );

    const textFieldProps = useMemo(
        () => ({
            InputLabelProps: contactId ? { shrink: true } : undefined,
            onFocus: handleFocus,
            onBlur: handleBlur,
            InputProps: {
                startAdornment: showCardOverlay ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            pl: '14px',
                            pr: '48px',
                            pointerEvents: 'none',
                            zIndex: 1,
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box
                            sx={{
                                minWidth: 0,
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden',
                            }}
                        >
                            <MemberLightCard noLink record={selected!} />
                        </Box>
                    </Box>
                ) : undefined,
            },
        }),
        [contactId, handleFocus, handleBlur, showCardOverlay, selected]
    );

    return (
        <AutocompleteInput
            disabled={props.disabled}
            label={props.label ?? 'Mission contact'}
            optionText={memberOptionText}
            inputText={emptyInputText}
            filterToQuery={search => ({ search })}
            helperText={props.helperText ?? false}
            TextFieldProps={textFieldProps}
            onChange={handleChange}
        />
    );
};
